/*
 * The postMessage bridge a host platform drives the editor with.
 *
 * When a host embeds the editor in an <iframe>, it is a separate window: the
 * host cannot set window.scratchConfig inside it, and it cannot call the
 * window.scratch API either, because the same-origin policy applies. So the two
 * sides talk instead. This module is the editor's half of that conversation.
 *
 * The protocol is not invented here. The teaching platform already speaks it,
 * and these are exactly the messages it sends and expects back:
 *
 *   host   -> editor   kidsblock.ping                    {workspaceId}
 *   host   -> editor   kidsblock.project.export.request  {workspaceId, requestId}
 *   editor -> host     kidsblock.ready                   {workspaceId}
 *   editor -> host     kidsblock.account.request         {workspaceId}
 *   editor -> host     kidsblock.project.export.result   {workspaceId, requestId,
 *                                                         success, sb3Base64}
 *   editor -> host     kidsblock.project.save.request    {workspaceId, requestId,
 *                                                         intent, success,
 *                                                         projectTitle,
 *                                                         sb3Base64}
 *   editor -> host     kidsblock.project.import.result   {workspaceId, success,
 *                                                         error}
 *
 * export.request is host-initiated: the host's own Save button asks the editor
 * for the bytes. save.request is the mirror image, sent when the user presses
 * the Save button the editor draws while embedded. `intent` is `save` or
 * `submit`, so a host can store the project and, for `submit`, also record it as
 * handed in.
 *
 * Every message carries bridgeVersion so a host can keep talking to an older
 * editor after a rollout, and workspaceId so one host can tell its sessions
 * apart. The editor treats workspaceId as opaque: it echoes it back and never
 * interprets it.
 *
 * The host reaches the editor with a URL rather than a message, because there is
 * no handshake early enough to send configuration:
 *
 *   /?workspaceId=...&embedded=1[&projectUrl=...][&projectTitle=...]
 *
 * The bridge stays dormant unless `embedded=1` is present, so the editor opened
 * on its own — or opened by an unrelated page in an iframe — never listens for
 * messages it did not ask for.
 */

const BRIDGE_VERSION = 1;

const PARAM_EMBEDDED = 'embedded';
const PARAM_WORKSPACE = 'workspaceId';
const PARAM_PROJECT_URL = 'projectUrl';
const PARAM_PROJECT_TITLE = 'projectTitle';
const PARAM_USERNAME = 'username';
const PARAM_AVATAR = 'avatar';

const RECEIVED = {
    PING: 'kidsblock.ping',
    EXPORT_REQUEST: 'kidsblock.project.export.request'
};

const SENT = {
    READY: 'kidsblock.ready',
    ACCOUNT_REQUEST: 'kidsblock.account.request',
    EXPORT_RESULT: 'kidsblock.project.export.result',
    SAVE_REQUEST: 'kidsblock.project.save.request',
    IMPORT_RESULT: 'kidsblock.project.import.result'
};

const INTENT_SAVE = 'save';
const INTENT_SUBMIT = 'submit';

// How long to wait for the VM before answering `ready` anyway. The host shows a
// load failure of its own after ~12s, so answering late is the same as not
// answering; answering slightly early is recoverable, because the export path
// reports its own failure.
const READY_DEADLINE_MS = 9000;
const READY_POLL_MS = 150;

const isAbsoluteUrl = url => /^[a-z][a-z0-9+.-]*:/i.test(url);

// Blob -> base64 without blowing the call stack on a multi-megabyte project.
const bytesToBase64 = bytes => {
    const CHUNK = 0x8000;
    let binary = '';
    for (let offset = 0; offset < bytes.length; offset += CHUNK) {
        binary += String.fromCharCode.apply(null, bytes.subarray(offset, offset + CHUNK));
    }
    return btoa(binary);
};

// saveProjectSb3 returns a Blob in current versions and raw bytes in older ones.
const toBytes = value => {
    if (!value) return Promise.resolve(new Uint8Array(0));
    if (value instanceof Uint8Array) return Promise.resolve(value);
    if (typeof value.arrayBuffer === 'function') {
        return value.arrayBuffer().then(buffer => new Uint8Array(buffer));
    }
    return Promise.resolve(new Uint8Array(0));
};

/*
 * Parsed by hand rather than with URLSearchParams: the editor ships to browsers
 * where it may be missing, and the test environment does not provide it either.
 */
const parseQuery = search => {
    const params = {};
    if (typeof search !== 'string') return params;
    const pairs = search.replace(/^\?/, '').split('&');
    pairs.forEach(pair => {
        if (!pair) return;
        const separator = pair.indexOf('=');
        const key = separator < 0 ? pair : pair.slice(0, separator);
        const value = separator < 0 ? '' : pair.slice(separator + 1);
        try {
            params[decodeURIComponent(key.replace(/\+/g, ' '))] =
                decodeURIComponent(value.replace(/\+/g, ' '));
        } catch (error) {
            params[key] = value;
        }
    });
    return params;
};

// `document.referrer` is the only hint at the host's origin before it speaks.
const originOf = url => {
    const match = /^([a-z][a-z0-9+.-]*:\/\/[^/?#]*)/i.exec(typeof url === 'string' ? url : '');
    return match ? match[1] : '';
};

const readEnvironment = (search, referrer) => {
    if (typeof search !== 'string') return null;
    const params = parseQuery(search);
    if (params[PARAM_EMBEDDED] !== '1') return null;
    return {
        workspaceId: params[PARAM_WORKSPACE] || '',
        projectUrl: params[PARAM_PROJECT_URL] || '',
        projectTitle: params[PARAM_PROJECT_TITLE] || '',
        // The host that renders its own page can hand the identity over in
        // `window.scratchConfig`, but an iframe host cannot write into this
        // document at all - so it passes the same identity on the URL.
        username: params[PARAM_USERNAME] || '',
        avatar: params[PARAM_AVATAR] || '',
        parentOrigin: originOf(referrer)
    };
};

/*
 * A path-only projectUrl (`/public-kidsblock-projects/12`) is served by the host,
 * not by us, so it has to be resolved against the host's origin. An absolute URL
 * is the host's storage and is used as given.
 */
const resolveProjectUrl = (url, parentOrigin) => {
    if (!url) return '';
    if (isAbsoluteUrl(url)) return url;
    if (url.indexOf('//') === 0) return `https:${url}`;
    if (url.charAt(0) === '/' && parentOrigin) return `${parentOrigin}${url}`;
    return url;
};

/*
 * The bridge as a testable object. `install()` wires one of these to the live
 * editor; the unit tests build their own with fake windows and timers.
 */
const createBridge = options => {
    const settings = options || {};
    const environment = settings.environment || {};
    const hostWindow = settings.window || (typeof window === 'undefined' ? null : window);
    const vm = settings.vm || null;
    const loadProjectFromUrl = settings.loadProject || null;
    const setProjectTitle = settings.setProjectTitle || (() => {});
    const getProjectTitle = settings.getProjectTitle || (() => '');
    const schedule = settings.setTimeout || (typeof setTimeout === 'undefined' ? null : setTimeout);
    const clearSchedule = settings.clearTimeout || (typeof clearTimeout === 'undefined' ? null : clearTimeout);

    const workspaceId = environment.workspaceId || '';
    let parentOrigin = environment.parentOrigin || '';
    let parent = null;
    let ready = false;
    let readyAnnounced = false;
    let importSettled = !environment.projectUrl;
    let poller = null;
    let deadline = null;

    const isExportable = () => {
        try {
            return Boolean(vm) && typeof vm.saveProjectSb3 === 'function' && Boolean(vm.runtime);
        } catch (error) {
            return false;
        }
    };

    const send = (type, payload) => {
        if (!parent || typeof parent.postMessage !== 'function') return false;
        const message = Object.assign({
            type,
            bridgeVersion: BRIDGE_VERSION,
            workspaceId
        }, payload || {});
        // Reply to the origin that spoke to us. Before anyone has spoken, the
        // referrer is the only hint we have, and if that is also missing '*' is
        // safe here because nothing in these messages is secret: the host gave
        // us the workspaceId and asked for the project it is about to receive.
        parent.postMessage(message, parentOrigin || '*');
        return true;
    };

    /*
     * Deliberately not idempotent. The host attaches its message listener from
     * its own mount effect, so our first `ready` can land before anyone is
     * listening; if we suppressed later ones the handshake would deadlock and
     * the host would give up on its ~12s timeout. The host treats `ready` as
     * idempotent, so repeating it is free.
     */
    const announceReady = () => {
        if (!ready || !importSettled) return false;
        readyAnnounced = send(SENT.READY, {}) || readyAnnounced;
        return readyAnnounced;
    };

    const stopTimers = () => {
        if (poller && clearSchedule) clearSchedule(poller);
        if (deadline && clearSchedule) clearSchedule(deadline);
        poller = null;
        deadline = null;
    };

    const markReady = () => {
        if (ready) return;
        ready = true;
        stopTimers();
        // Best effort, so a host that is already listening hears sooner than its
        // ping would arrive. A lost first attempt is harmless now.
        announceReady();
    };

    const pollUntilExportable = () => {
        if (!schedule) {
            markReady();
            return;
        }
        deadline = schedule(() => markReady(), READY_DEADLINE_MS);
        const tick = () => {
            if (ready) return;
            if (isExportable()) {
                markReady();
                return;
            }
            poller = schedule(tick, READY_POLL_MS);
        };
        tick();
    };

    const accept = event => {
        if (!event || event.source !== parent) return false;
        const data = event.data;
        if (!data || data.bridgeVersion !== BRIDGE_VERSION) return false;
        if (data.workspaceId !== workspaceId) return false;
        return true;
    };

    const handleExport = data => {
        if (!data.requestId) return;
        if (!isExportable()) {
            send(SENT.EXPORT_RESULT, {requestId: data.requestId, success: false});
            return;
        }
        Promise.resolve()
            .then(() => vm.saveProjectSb3())
            .then(toBytes)
            .then(bytes => {
                send(SENT.EXPORT_RESULT, {
                    requestId: data.requestId,
                    success: true,
                    sb3Base64: bytesToBase64(bytes)
                });
            })
            .catch(() => {
                send(SENT.EXPORT_RESULT, {requestId: data.requestId, success: false});
            });
    };

    const handleMessage = event => {
        if (!accept(event)) return false;
        // Learn the host's origin from the first thing it says, so every reply
        // can be addressed precisely instead of broadcast.
        if (event.origin) parentOrigin = event.origin;
        const data = event.data;
        if (data.type === RECEIVED.PING) {
            announceReady();
            return true;
        }
        if (data.type === RECEIVED.EXPORT_REQUEST) {
            handleExport(data);
            return true;
        }
        return false;
    };

    const runImport = () => {
        const url = resolveProjectUrl(environment.projectUrl, parentOrigin);
        if (!url || typeof loadProjectFromUrl !== 'function') {
            importSettled = true;
            return Promise.resolve();
        }
        return Promise.resolve()
            .then(() => loadProjectFromUrl(url))
            .then(() => {
                if (environment.projectTitle) setProjectTitle(environment.projectTitle);
                importSettled = true;
                announceReady();
                send(SENT.IMPORT_RESULT, {success: true});
            })
            .catch(error => {
                importSettled = true;
                announceReady();
                send(SENT.IMPORT_RESULT, {
                    success: false,
                    error: (error && error.message) || String(error)
                });
            });
    };

    const start = () => {
        if (!hostWindow || !hostWindow.parent || hostWindow.parent === hostWindow) return null;
        parent = hostWindow.parent;
        hostWindow.addEventListener('message', handleMessage);
        pollUntilExportable();
        runImport();
        return {
            workspaceId,
            projectTitle: environment.projectTitle || '',
            hasIncomingProject: Boolean(environment.projectUrl)
        };
    };

    const stop = () => {
        stopTimers();
        if (hostWindow) hostWindow.removeEventListener('message', handleMessage);
        parent = null;
    };

    // The host owns the account, so the editor can only report that the user
    // asked for it and let the host decide between "sign in" and "your account".
    const requestAccount = () => send(SENT.ACCOUNT_REQUEST, {});

    /*
     * The editor's own Save button. The export path is host-initiated, so the
     * host already holds a requestId for it; here the editor has to mint one,
     * because nothing on the host side is expecting this to arrive.
     */
    let saveCounter = 0;
    const requestSave = intent => {
        if (!parent) return false;
        saveCounter += 1;
        const payload = {
            requestId: `save-${Date.now()}-${saveCounter}`,
            intent: intent === INTENT_SUBMIT ? INTENT_SUBMIT : INTENT_SAVE
        };
        if (!isExportable()) {
            send(SENT.SAVE_REQUEST, Object.assign({success: false}, payload));
            return false;
        }
        Promise.resolve()
            .then(() => vm.saveProjectSb3())
            .then(toBytes)
            .then(bytes => {
                send(SENT.SAVE_REQUEST, Object.assign({
                    success: true,
                    projectTitle: getProjectTitle(),
                    sb3Base64: bytesToBase64(bytes)
                }, payload));
            })
            .catch(() => {
                send(SENT.SAVE_REQUEST, Object.assign({success: false}, payload));
            });
        return true;
    };

    const nameStatus = () => ({
        ready,
        readyAnnounced,
        importSettled,
        parentOrigin,
        workspaceId,
        projectTitle: getProjectTitle()
    });

    return {
        SENT,
        handleMessage,
        nameStatus,
        ready: () => ready,
        requestAccount,
        requestSave,
        start,
        stop,
        workspaceId
    };
};

let current = null;

const uninstall = () => {
    if (current) current.stop();
    current = null;
};

// The live edge, used with `window` and real timers. Returns null when the page
// was not loaded as an embed, which is the common case for the standalone editor.
const install = options => {
    uninstall();
    const settings = options || {};
    const hostWindow = settings.window || (typeof window === 'undefined' ? null : window);
    if (!hostWindow) return null;
    const environment = readEnvironment(hostWindow.location.search, settings.referrer || '');
    if (!environment) return null;
    current = createBridge(Object.assign({}, settings, {
        environment,
        window: hostWindow
    }));
    const started = current.start();
    if (!started) {
        current = null;
        return null;
    }
    return started;
};

const isEmbedded = () => Boolean(current);

const requestAccount = () => {
    if (current) current.requestAccount();
};

const requestSave = intent => {
    if (current) return current.requestSave(intent);
    return false;
};

// Lets the menu bar offer an account entry only when a host is on the other end.
const getWorkspace = () => (current ? {
    workspaceId: current.workspaceId,
    ready: current.ready()
} : null);

export {
    BRIDGE_VERSION,
    INTENT_SAVE,
    INTENT_SUBMIT,
    RECEIVED,
    SENT,
    createBridge,
    getWorkspace,
    install,
    isEmbedded,
    readEnvironment,
    requestAccount,
    requestSave,
    resolveProjectUrl,
    uninstall
};
